import { useEffect, useRef, useState } from "react"
import parse from "html-react-parser"

import ServiceBrand from "./shared/service_brand"
import SuggestionModal from "shared/components/suggestion_modal"
import { useBgColorContext } from "./context"
import { headerColored } from "./utils/container_background_color"
import { useGradientBand } from "./utils/gradient_band"
import { iftttAiGradient, iftttAiGradientBackground } from "shared/utils/ifttt_logo"
import { pluralizeStepType } from "shared/lib/utils"
import plusIcon from "images/icons/plus.svg?raw"
import realtimeIcon from "images/icons/icon-realtime.svg?raw"
import pollingIcon from "images/icons/icon-polling.svg?raw"
import { tierBadge } from "shared/utils/tiers"

import styles from "./step_selector.module.scss"
import classNames from "classnames"
import { Service, TQAType } from "./utils/interfaces"

interface StepSelectorProps {
  onStepSelect: (v: TQAType) => void
  service: Service
  steps: []
  selectedSteps: Array<TQAType> | []
  stepType: "trigger" | "query" | "action"
}

interface SuggestionCardProps {
  stepType: "trigger" | "query" | "action"
  onClick: () => void
}

const SuggestionCard = ({ stepType, onClick }: SuggestionCardProps) => (
  <li className={styles["suggestion-card"]} role="listitem">
    <button type="button" title={`Suggest a new ${stepType}`} onClick={onClick}>
      <span className={styles.plus}>{parse(plusIcon)}</span>
      <span className={classNames("text-h2 phone-text-h4", styles.title)}>Suggest a new {stepType}</span>
    </button>
  </li>
)

export default function StepSelector({ onStepSelect, service, selectedSteps, steps, stepType }: StepSelectorProps) {
  const bgColor = useBgColorContext()
  const [showSuggestionModal, setShowSuggestionModal] = useState(false)
  const brandRef = useRef<HTMLElement>(null)
  const gradient = iftttAiGradient(service.module_name)
  useGradientBand(brandRef, gradient)

  useEffect(() => {
    headerColored(gradient ? "white" : bgColor)

    return () => {
      headerColored("initial")
    }
  }, [bgColor, gradient])

  const identifiersFromSelectedSteps = selectedSteps.map((step: TQAType) => step.full_module_name)

  const stepsAvailables = steps
    .filter((step: TQAType) => !identifiersFromSelectedSteps.includes(step.full_module_name))
    .sort((a, b) => a["weight"] - b["weight"])

  const tile = (step: TQAType) => {
    const onStepClicked = () => {
      onStepSelect({ ...step, service })
    }
    const instant = stepType === "trigger" && "instant" in step ? step.instant : undefined

    return (
      <li
        key={step.id}
        style={{
          backgroundColor: service.brand_color,
          ...(gradient ? iftttAiGradientBackground("tqa_card") : {}),
        }}
        role="listitem"
      >
        <button
          type="button"
          title={`Choose ${stepType}: ${step.name}`}
          className={classNames({ [styles.badged]: step.tier !== "free" })}
          onClick={onStepClicked}
        >
          <span className={classNames("text-h3", styles.title)}>{step.name}</span>
          {typeof instant === "boolean" && (
            <span className={styles.chips}>
              <span className={styles.chip}>
                {parse(instant ? realtimeIcon : pollingIcon)}
                {instant ? "Realtime trigger" : "Polling trigger"}
              </span>
            </span>
          )}
          <span className={classNames("body-medium", styles.description)}>{step.description}</span>
          {/* Inside the button so a click on the badge still selects the step. */}
          {step.tier !== "free" && (
            <span title={`This ${stepType} has pro features`} className="card-badge fixed">
              {parse(tierBadge(step.tier))}
            </span>
          )}
        </button>
      </li>
    )
  }

  return (
    <>
      {showSuggestionModal && (
        <SuggestionModal
          channelId={service.id}
          onClose={() => setShowSuggestionModal(false)}
          feedbackTqaType={stepType}
          serviceName={service.name}
        />
      )}
      <div className={styles["step-selector"]}>
        <section ref={brandRef} className={styles.brand} style={{ backgroundColor: bgColor }}>
          <ServiceBrand title={service.name} image={service.image} />
        </section>
        {stepsAvailables.length > 0 ? (
          <ul role="list">
            {stepsAvailables.map(step => tile(step))}
            <SuggestionCard stepType={stepType} onClick={() => setShowSuggestionModal(true)} />
          </ul>
        ) : (
          <span className={styles["empty-list"]}>
            You have selected all {pluralizeStepType(stepType)} from this service
          </span>
        )}
      </div>
    </>
  )
}
